import type { OrderStatus } from '@pizzeria/types/order';
import type { Database } from '@pizzeria/types/database.types';

import { supabaseAdmin } from '../lib/supabaseClient';

type PaymentMethod = 'cash' | 'credit_card' | 'online_card' | 'meal_card' | 'yemeksepeti';
type PaymentStatus = 'pending' | 'completed' | 'failed' | 'refunded' | 'cancelled';
type JsonRecord = Record<string, unknown>;

type ExternalOrderRow = {
  internal_order_id: string | null;
  external_order_code: string | null;
  raw_payload: unknown;
};

type OrderRow = Database['public']['Tables']['orders']['Row'] & {
  source_platform?: string | null;
  source_external_order_id?: string | null;
  users: Database['public']['Tables']['users']['Row'] | null;
  addresses: Database['public']['Tables']['addresses']['Row'] | null;
  restaurants: Database['public']['Tables']['restaurants']['Row'] | null;
  order_items: Array<
    Database['public']['Tables']['order_items']['Row'] & {
      products: Database['public']['Tables']['products']['Row'] | null;
    }
  > | null;
  order_delivery_tracking: Array<
    Database['public']['Tables']['order_delivery_tracking']['Row'] & {
      drivers: Database['public']['Tables']['drivers']['Row'] | null;
    }
  > | null;
};

interface OrderListParams {
  limit?: number;
}

export interface OrderListRecord {
  id: string;
  createdAt: string;
  updatedAt: string | null;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod;
  iyzicoPaymentId?: string | null;
  totalAmount: number;
  notes: string | null;
  sourcePlatform: string | null;
  sourceExternalOrderId: string | null;
  customer: {
    name: string | null;
    email: string | null;
    phone: string | null;
  };
  restaurant: {
    name: string | null;
    slug: string | null;
  };
  driver: {
    name: string | null;
    phone: string | null;
    status: string | null;
    updatedAt: string | null;
    deliveredAt: string | null;
  };
  addressSummary: string | null;
  itemSummary: string;
}

const ORDER_SELECT = `
  id,
  user_id,
  address_id,
  total_amount,
  status,
  payment_status,
  payment_method,
  iyzico_payment_id,
  notes,
  guest_name,
  guest_phone,
  restaurant_id,
  source_platform,
  source_external_order_id,
  created_at,
  updated_at,
  users:users (
    id,
    full_name,
    email,
    phone
  ),
  addresses:addresses (
    id,
    title,
    address_line,
    district,
    city
  ),
  restaurants:restaurants (
    id,
    name,
    slug
  ),
  order_items (
    id,
    quantity,
    products:products (
      id,
      name
    )
  ),
  order_delivery_tracking (
    id,
    driver_status,
    driver_status_updated_at,
    actual_delivery_time,
    driver_comment,
    delivery_person_name,
    created_at,
    updated_at,
    drivers:drivers (
      id,
      full_name,
      phone
    )
  )
`;

async function fetchOrdersFromSupabase(limit: number) {
  return await supabaseAdmin
    .from('orders')
    .select(ORDER_SELECT)
    .order('created_at', { ascending: false })
    .limit(limit);
}

async function fetchExternalOrdersFromSupabase(orderIds: string[]) {
  if (orderIds.length === 0) {
    return [] as ExternalOrderRow[];
  }

  const { data, error } = await (supabaseAdmin as any)
    .from('external_orders')
    .select('internal_order_id, external_order_code, raw_payload')
    .in('internal_order_id', orderIds);

  if (error) {
    throw error;
  }

  return (data ?? []) as ExternalOrderRow[];
}

const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  cash: 'Nakit ödeme',
  credit_card: 'Kart',
  online_card: 'Online ödeme (iyzico)',
  meal_card: 'Yemek kartı',
  yemeksepeti: 'YS Vale',
};

// Treat credit_card with iyzico_payment_id as online for labeling
export function paymentMethodLabelWithIyzico(
  method: PaymentMethod,
  iyzicoPaymentId?: string | null
): string {
  if (method === 'credit_card' && iyzicoPaymentId) {
    return PAYMENT_LABELS.online_card;
  }
  return PAYMENT_LABELS[method] ?? method;
}

const asRecord = (value: unknown): JsonRecord | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as JsonRecord) : null;

const asString = (value: unknown): string | null => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (typeof value === 'number' || typeof value === 'bigint') {
    return String(value);
  }

  return null;
};

const parsePositiveInteger = (value: unknown, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }
  return Math.max(1, Math.round(parsed));
};

const normalizeTextKey = (value: string) => value.toLocaleLowerCase('tr-TR').replace(/\s+/g, ' ').trim();

const uniqueTextValues = (values: Array<string | null | undefined>) => {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const value of values) {
    if (typeof value !== 'string') {
      continue;
    }

    const trimmed = value.trim();
    if (!trimmed) {
      continue;
    }

    const key = normalizeTextKey(trimmed);
    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    result.push(trimmed);
  }

  return result;
};

const parseMarketplacePayloadItems = (value: unknown) => {
  const payload = asRecord(value);
  if (!payload || !Array.isArray(payload.items)) {
    return [] as Array<{ name: string; quantity: number }>;
  }

  return payload.items
    .map((entry) => {
      const item = asRecord(entry);
      const name = asString(item?.name);
      if (!name) {
        return null;
      }

      return {
        name,
        quantity: parsePositiveInteger(item?.amount, 1),
      };
    })
    .filter((entry): entry is { name: string; quantity: number } => Boolean(entry));
};

const summarizeMarketplaceAddress = (value: unknown): string | null => {
  const payload = asRecord(value);
  if (!payload) {
    return null;
  }

  const address =
    asRecord(payload.address) ??
    asRecord(payload.customerAddress) ??
    asRecord(payload.customer_address) ??
    asRecord(payload.deliveryAddress) ??
    asRecord(payload.delivery_address);

  if (!address) {
    return null;
  }

  const street =
    asString(address.street) ??
    asString(address.addressLine) ??
    asString(address.address_line);
  const district =
    asString(address.area) ??
    asString(address.district) ??
    asString(address.region);
  const city =
    asString(address.city) ??
    asString(address.cityName) ??
    asString(address.town);
  const building =
    asString(address.building) ??
    asString(address.buildingNumber) ??
    asString(address.building_number);
  const floor =
    asString(address.floor) ??
    asString(address.floorNumber) ??
    asString(address.floor_number);
  const apartment =
    asString(address.entrance) ??
    asString(address.apartmentNumber) ??
    asString(address.apartment_number);
  const buildingName =
    asString(address.buildingName) ??
    asString(address.building_name);
  const company = asString(address.company);
  const instructions =
    asString(address.info) ??
    asString(address.instructions) ??
    asString(address.note) ??
    asString(address.description);

  const summary = uniqueTextValues([
    street,
    buildingName,
    building ? `No: ${building}` : null,
    floor ? `Kat: ${floor}` : null,
    apartment ? `Daire: ${apartment}` : null,
    district,
    city,
    company,
    instructions,
  ]);

  return summary.length > 0 ? summary.join(', ') : null;
};

const summarizeAddress = (address: OrderRow['addresses']): string | null => {
  if (!address) return null;
  const parts = [address.address_line, address.district, address.city]
    .filter((part): part is string => Boolean(part && part.trim().length > 0));
  return parts.length > 0 ? parts.join(', ') : null;
};

const summarizeItems = (items: OrderRow['order_items'], externalOrder: ExternalOrderRow | null): string => {
  if (Array.isArray(items) && items.length > 0) {
    const summary = items
      .map((item) => {
        const qty = item.quantity ?? 0;
        const productName = item.products?.name ?? 'Ürün';
        return `${qty}× ${productName}`;
      })
      .slice(0, 3)
      .join(', ');
    return items.length > 3 ? `${summary} +${items.length - 3} ürün` : summary;
  }

  const marketplaceItems = parseMarketplacePayloadItems(externalOrder?.raw_payload);
  if (marketplaceItems.length === 0) {
    return 'Ürün bilgisi yok';
  }

  const summary = marketplaceItems
    .map((item) => `${item.quantity}× ${item.name}`)
    .slice(0, 3)
    .join(', ');

  return marketplaceItems.length > 3 ? `${summary} +${marketplaceItems.length - 3} ürün` : summary;
};

const pickLatestTracking = (tracking: OrderRow['order_delivery_tracking']) => {
  if (!Array.isArray(tracking) || tracking.length === 0) {
    return null;
  }
  return tracking.reduce((latest, entry) => {
    if (!latest) return entry;
    const latestTime = latest.driver_status_updated_at ?? latest.updated_at ?? latest.created_at ?? '';
    const entryTime = entry.driver_status_updated_at ?? entry.updated_at ?? entry.created_at ?? '';
    return entryTime > latestTime ? entry : latest;
  }, tracking[0]);
};

const mapOrderRecord = (row: OrderRow, externalOrder: ExternalOrderRow | null): OrderListRecord => {
  const latestTracking = pickLatestTracking(row.order_delivery_tracking ?? []);
  const driver = latestTracking?.drivers ?? null;
  const deliveredAt =
    latestTracking?.driver_status === 'delivered'
      ? latestTracking.actual_delivery_time ?? latestTracking.driver_status_updated_at ?? latestTracking.updated_at ?? null
      : null;
  return {
    id: row.id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    status: row.status as OrderStatus,
    paymentStatus: row.payment_status as PaymentStatus,
    paymentMethod: row.payment_method as PaymentMethod,
    iyzicoPaymentId: (row as any).iyzico_payment_id ?? null,
    totalAmount: Number(row.total_amount ?? 0),
    notes: row.notes ?? null,
    sourcePlatform: row.source_platform ?? null,
    sourceExternalOrderId: row.source_external_order_id ?? null,
    customer: {
      name: row.users?.full_name ?? row.guest_name ?? null,
      email: row.users?.email ?? null,
      phone: row.users?.phone ?? row.guest_phone ?? null,
    },
    restaurant: {
      name: row.restaurants?.name ?? null,
      slug: row.restaurants?.slug ?? null,
    },
    driver: {
      name: driver?.full_name ?? latestTracking?.delivery_person_name ?? null,
      phone: driver?.phone ?? null,
      status: latestTracking?.driver_status ?? null,
      updatedAt: latestTracking?.driver_status_updated_at ?? latestTracking?.updated_at ?? null,
      deliveredAt,
    },
    addressSummary: summarizeAddress(row.addresses) ?? summarizeMarketplaceAddress(externalOrder?.raw_payload),
    itemSummary: summarizeItems(row.order_items ?? [], externalOrder),
  };
};

export async function listRecentOrders(params: OrderListParams = {}): Promise<OrderListRecord[]> {
  const limit = params.limit ?? 150;
  const { data, error } = await fetchOrdersFromSupabase(limit);

  if (error) {
    throw error;
  }

  const orders = (data ?? []) as unknown as OrderRow[];
  const marketplaceOrderIds = orders
    .filter((order) => typeof order.source_platform === 'string' && order.source_platform.trim().length > 0)
    .map((order) => order.id);
  const externalOrders = await fetchExternalOrdersFromSupabase(marketplaceOrderIds);
  const externalOrderByInternalOrderId = new Map<string, ExternalOrderRow>();

  externalOrders.forEach((externalOrder) => {
    if (!externalOrder.internal_order_id) {
      return;
    }
    externalOrderByInternalOrderId.set(externalOrder.internal_order_id, externalOrder);
  });

  return orders.map((order) => mapOrderRecord(order, externalOrderByInternalOrderId.get(order.id) ?? null));
}

export function paymentMethodLabel(method: PaymentMethod): string {
  return PAYMENT_LABELS[method] ?? method;
}
